import {
  accountFromSession,
  resetKey,
  type CacheAccountSnapshot,
  type SessionAnswer,
} from '../../lib/cache-account';
import type { TabsSnapshot } from '../../lib/identity-tabs';
import type { Session } from '../session';

/**
 * What the root gate shows, as a pure function of what it knows (MAP 225a).
 *
 * Kept out of the component so every branch can be driven by a REAL `QueryObserver`'s
 * result in a test: `apps/web` has no DOM, and the decision is the security property.
 *
 * - **Online** (anything but an explicit `navigator.onLine === false`, which TanStack's
 *   `onlineManager` now carries, MAP 225b): nothing of a shop's
 *   is shown until the network has answered `me.session` in this page's life. `me.session` is
 *   never saved, so `session.data` existing IS that answer. And it shows only once the saved
 *   cache belongs to the account the network named, in the same role, the old rows emptied
 *   and off the disk first (`CacheAccount.confirm`). A failure or a timeout keeps today's
 *   skeleton, then today's error screen.
 * - **Offline**: nobody new can have signed in without a connection, so the saved account's
 *   screens are drawn from cache, as the basement case needs (ruling R1).
 * - **Another tab changed account**: a tab that was drawing draws nothing of it again
 *   (stopped); a tab that was drawing nothing draws no shop until that change is announced
 *   (held).
 */

export type GateInput = {
  isPublic: boolean;
  /** `navigator.onLine === false`, and only that (through `onlineManager`). */
  offline: boolean;
  /** The persister is still reading the saved cache, so its account is not known yet. */
  restoring: boolean;
  /** The `me.session` observer's result: pending is "no data and no error". */
  session: { data: SessionAnswer | undefined; isError: boolean };
  cache: CacheAccountSnapshot;
  tabs: TabsSnapshot;
};

export type Gate =
  | { kind: 'public' }
  | { kind: 'stopped'; reason: NonNullable<TabsSnapshot['stopped']> }
  | { kind: 'cannot-clear' }
  | { kind: 'loading' }
  | { kind: 'unreachable' }
  | { kind: 'sign-in' }
  | { kind: 'create-shop' }
  | { kind: 'no-role' }
  | { kind: 'shop'; session: Session };

export function decideGate(input: GateInput): Gate {
  const { session, cache, tabs } = input;
  if (tabs.stopped) return { kind: 'stopped', reason: tabs.stopped };
  if (input.isPublic) return { kind: 'public' };
  const data = session.data;
  // The previous account's rows may still be on this device: nothing goes over them.
  // Online with no answer yet (a start after a purge failed), wait for one: it runs the
  // purge again.
  if (cache.status === 'failed' && (input.offline || data !== undefined)) {
    return { kind: 'cannot-clear' };
  }
  // A network answer from this page's life. Online, a failed refetch still shows the error
  // screen, as it always has; offline, that failure is the missing signal, not news.
  if (data !== undefined && (input.offline || !session.isError)) {
    const account = accountFromSession(data);
    if (cache.status !== 'settled' || resetKey(cache.account) !== resetKey(account)) {
      return { kind: 'loading' };
    }
    if (!data.authenticated) return { kind: 'sign-in' };
    if (!data.orgId) return { kind: 'create-shop' };
    // A member with no recognized role is a server-side state we must not paper over
    // by assuming "office" — that would paint chrome for permissions they may not have.
    if (!account) return { kind: 'no-role' };
    if (tabs.held) return { kind: 'loading' };
    return { kind: 'shop', session: account };
  }

  if (input.offline && !input.restoring && !tabs.held && cache.account) {
    return { kind: 'shop', session: cache.account };
  }
  if (session.isError) return { kind: 'unreachable' };
  return { kind: 'loading' };
}
